/** Shared viewport-clamped dragging for the feedback FAB and floating surfaces. */
import { useCallback, useEffect, useRef, useState } from 'react'

const DRAG_THRESHOLD_PX = 4
const VIEWPORT_MARGIN = 8
export type FabPosition = { left: number; top: number }
function clampToViewport(pos: FabPosition, size: { w: number; h: number }): FabPosition {
  return {
    left: Math.min(
      Math.max(VIEWPORT_MARGIN, pos.left),
      Math.max(VIEWPORT_MARGIN, window.innerWidth - size.w - VIEWPORT_MARGIN)
    ),
    top: Math.min(
      Math.max(VIEWPORT_MARGIN, pos.top),
      Math.max(VIEWPORT_MARGIN, window.innerHeight - size.h - VIEWPORT_MARGIN)
    ),
  }
}
function loadPosition(key: string): FabPosition | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? 'null')
    if (Number.isFinite(parsed?.left) && Number.isFinite(parsed?.top)) return parsed
  } catch {
    /* Storage may be unavailable. */
  }
  return null
}
export function useDraggableFab<T extends HTMLElement = HTMLButtonElement>({
  storageKey = 'archer.feedbackFab.position',
  enabled = true,
}: { storageKey?: string; enabled?: boolean } = {}) {
  const ref = useRef<T | null>(null)
  const [position, setPosition] = useState<FabPosition | null>(() => loadPosition(storageKey))
  const draggedRef = useRef(false)
  const startRef = useRef<{ x: number; y: number; left: number; top: number } | null>(null)
  const cleanupRef = useRef<(() => void) | null>(null)
  const measure = useCallback(
    () =>
      ref.current ? { w: ref.current.offsetWidth, h: ref.current.offsetHeight } : { w: 40, h: 40 },
    []
  )
  const persist = useCallback(
    (value: FabPosition) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(value))
      } catch {
        /* Session position still works. */
      }
    },
    [storageKey]
  )
  useEffect(() => {
    if (!enabled) return
    const clamp = () =>
      setPosition(previous => (previous ? clampToViewport(previous, measure()) : previous))
    clamp()
    window.addEventListener('resize', clamp)
    const observer = new ResizeObserver(clamp)
    if (ref.current) observer.observe(ref.current)
    return () => {
      window.removeEventListener('resize', clamp)
      observer.disconnect()
    }
  }, [enabled, measure])
  useEffect(
    () => () => {
      cleanupRef.current?.()
    },
    []
  )
  useEffect(() => {
    if (!enabled) cleanupRef.current?.()
  }, [enabled])
  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (!enabled || event.button !== 0 || !ref.current) return
      cleanupRef.current?.()
      const rect = ref.current.getBoundingClientRect()
      startRef.current = { x: event.clientX, y: event.clientY, left: rect.left, top: rect.top }
      draggedRef.current = false
      const move = (e: PointerEvent) => {
        const start = startRef.current
        if (!start) return
        const dx = e.clientX - start.x,
          dy = e.clientY - start.y
        if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) draggedRef.current = true
        if (draggedRef.current)
          setPosition(clampToViewport({ left: start.left + dx, top: start.top + dy }, measure()))
      }
      const end = () => {
        cleanupRef.current?.()
        setPosition(previous => {
          if (previous) persist(previous)
          return previous
        })
      }
      const cleanup = () => {
        startRef.current = null
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', end)
        window.removeEventListener('pointercancel', end)
        cleanupRef.current = null
      }
      cleanupRef.current = cleanup
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', end)
      window.addEventListener('pointercancel', end)
    },
    [enabled, measure, persist]
  )
  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (draggedRef.current) {
      e.preventDefault()
      e.stopPropagation()
      draggedRef.current = false
    }
  }, [])
  const moveBy = useCallback(
    (x: number, y: number) => {
      if (!enabled || !ref.current) return
      const rect = ref.current.getBoundingClientRect()
      const next = clampToViewport({ left: rect.left + x, top: rect.top + y }, measure())
      setPosition(next)
      persist(next)
    },
    [enabled, measure, persist]
  )
  const style = position
    ? { top: position.top, left: position.left, bottom: 'auto', right: 'auto' }
    : { bottom: 24, right: 24 }
  return { ref, onPointerDown, onClickCapture, moveBy, style }
}
